import React, { useState, useEffect } from 'react';
import { Lightbulb, Settings, Users, Globe, ChevronDown, ChevronUp, ArrowRight, FileText, Save, RotateCcw, CheckCircle, History, Eye } from 'lucide-react';
import { problemDiagnosisService } from '../services/problemDiagnosisService';
import { SystemType, PrincipleQuestion, UserAnswer, DiagnosisReport } from '../types/problemDiagnosis';
import DiagnosisHistory from './DiagnosisHistory';
import DiagnosisDetailOptimized from './diagnosis/DiagnosisDetailOptimized';

// 系统定义接口
interface SystemDefinition {
  id: SystemType;
  name: string;
  icon: React.ReactNode;
  keywords: string;
  description: string;
  color: string;
  borderColor: string;
}

// 五大原理问题定义
interface PrincipleQuestion {
  id: string;
  principle: string;
  question: string;
  placeholder: string;
}

// 用户回答
interface UserAnswer {
  questionId: string;
  answer: string;
}

// 诊断报告
interface DiagnosisReport {
  title: string;
  coreConclusion: string;
  systemFindings: {
    system: string;
    finding: string;
  }[];
  nextSteps: {
    level: string;
    description: string;
  }[];
}

const ProblemDiagnosis: React.FC = () => {
  // 视图状态：main=主诊断流程, history=历史记录, detail=诊断明细
  const [currentView, setCurrentView] = useState<'main' | 'history' | 'detail'>('main');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState<'input' | 'triage' | 'analysis' | 'crossConsult' | 'report'>('input');
  const [problemDescription, setProblemDescription] = useState('');
  const [selectedSystem, setSelectedSystem] = useState<SystemDefinition | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [diagnosisReport, setDiagnosisReport] = useState<DiagnosisReport | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<PrincipleQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 四大系统定义
  const systems: SystemDefinition[] = [
    {
      id: 'machine',
      name: '机械系统 (The Machine)',
      icon: <Settings className="w-6 h-6" />,
      keywords: '效率、流程、规则、工具',
      description: '如果问题感觉像"流程不顺、规则混乱、工具不好用"，请选择这里。',
      color: 'border-blue-500',
      borderColor: 'border-blue-500'
    },
    {
      id: 'organism',
      name: '生理系统 (The Organism)',
      icon: <Lightbulb className="w-6 h-6" />,
      keywords: '能力、健康、发展、成长',
      description: '如果问题感觉像"组织没活力、核心能力不足、发展停滞"，请选择这里。',
      color: 'border-green-500',
      borderColor: 'border-green-500'
    },
    {
      id: 'social',
      name: '社会系统 (The Social System)',
      icon: <Users className="w-6 h-6" />,
      keywords: '人、关系、动机、文化',
      description: '如果问题感觉像"团队士气低、沟通不畅、人心不齐"，请选择这里。',
      color: 'border-purple-500',
      borderColor: 'border-purple-500'
    },
    {
      id: 'ecosystem',
      name: '生态系统 (The Ecosystem)',
      icon: <Globe className="w-6 h-6" />,
      keywords: '环境、竞争、趋势、市场',
      description: '如果问题感觉像"市场变化快、竞争太激烈、外部风险大"，请选择这里。',
      color: 'border-orange-500',
      borderColor: 'border-orange-500'
    }
  ];

  // 错误提示组件
  const ErrorMessage = ({ message }: { message: string }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p className="text-red-600 text-sm">{message}</p>
    </div>
  );

  // 加载状态组件
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span className="ml-2 text-gray-600">加载中...</span>
    </div>
  );

  // 五大原理问题（根据选择的系统动态生成）- 已废弃，现在从后端获取
  const getQuestionsForSystem = (systemId: string): PrincipleQuestion[] => {
    const baseQuestions = [
      {
        id: 'materiality',
        principle: '物质性 (构成要素是什么？)',
        question: '',
        placeholder: ''
      },
      {
        id: 'structure',
        principle: '结构性 (它们如何组织？)',
        question: '',
        placeholder: ''
      },
      {
        id: 'relationship',
        principle: '关系性 (它们如何互动？)',
        question: '',
        placeholder: ''
      },
      {
        id: 'dynamics',
        principle: '动态性 (它如何演变？)',
        question: '',
        placeholder: ''
      },
      {
        id: 'systemic',
        principle: '系统性 (整体涌现出什么？)',
        question: '',
        placeholder: ''
      }
    ];

    // 根据系统类型定制问题
    switch (systemId) {
      case 'social':
        return [
          {
            ...baseQuestions[0],
            question: '首先，这个问题涉及到的所有关键方（Stakeholders）是谁？请把他们都列出来。',
            placeholder: '例如：客户、我的老板、下属、其他部门同事等'
          },
          {
            ...baseQuestions[1],
            question: '这些关键方之间的权力结构和影响力网络是怎样的？谁是决策者？谁是影响者？谁说了算？',
            placeholder: '请描述权力结构和影响力关系...'
          },
          {
            ...baseQuestions[2],
            question: '他们之间的关系是合作、竞争还是冲突？彼此的信任水平如何？是否存在影响他们互动的"潜规则"？',
            placeholder: '请分析关系性质和互动模式...'
          },
          {
            ...baseQuestions[3],
            question: '驱动这些关键方做出当前行为的深层需求和内在动机是什么？他们最关心什么，最害怕什么？',
            placeholder: '请分析深层动机和需求...'
          },
          {
            ...baseQuestions[4],
            question: '在这些互动之下，整个团队或组织涌现出了什么样的整体文化或氛围？',
            placeholder: '例如：是开放信任，还是猜忌保守？'
          }
        ];
      case 'machine':
        return [
          {
            ...baseQuestions[0],
            question: '这个问题涉及到的所有关键流程、规则、工具和系统是什么？请把它们都列出来。',
            placeholder: '例如：审批流程、考核规则、IT系统、工作工具等'
          },
          {
            ...baseQuestions[1],
            question: '这些流程和规则是如何组织和设计的？它们的层级结构和逻辑关系是什么？',
            placeholder: '请描述流程结构和规则体系...'
          },
          {
            ...baseQuestions[2],
            question: '这些流程、规则、工具之间是如何相互配合或冲突的？哪些环节存在断点或重复？',
            placeholder: '请分析流程间的配合和冲突...'
          },
          {
            ...baseQuestions[3],
            question: '这些机械系统是如何随时间演变的？什么因素驱动了它们的变化？',
            placeholder: '请分析系统演变的驱动因素...'
          },
          {
            ...baseQuestions[4],
            question: '整个机械系统运行的结果是什么？它产生了什么样的整体效率和效果？',
            placeholder: '请评估整体系统效果...'
          }
        ];
      default:
        return baseQuestions;
    }
  };

  const handleStartAnalysis = async () => {
    if (!problemDescription.trim()) {
      setError('请先描述您遇到的问题');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 创建新的诊断会话
      const session = await problemDiagnosisService.createSession(problemDescription);
      setSessionId(session.id);
      setCurrentStep('triage');
    } catch (err) {
      setError('创建诊断会话失败，请重试');
      console.error('创建会话失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSelect = async (system: SystemDefinition) => {
    setLoading(true);
    setError(null);

    try {
      // 获取该系统的问题模板
      const systemQuestions = await problemDiagnosisService.getQuestions(system.id);
      setQuestions(systemQuestions);

      // 更新会话信息
      if (sessionId) {
        await problemDiagnosisService.updateSession(sessionId, {
          selected_system: system.id,
          current_step: 'analysis'
        });
      }

      setSelectedSystem(system);
      setCurrentStep('analysis');
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setCurrentAnswer('');
    } catch (err) {
      setError('获取问题模板失败，请重试');
      console.error('获取问题失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = async () => {
    if (!currentAnswer.trim()) {
      setError('请回答当前问题后再继续');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newAnswer: UserAnswer = {
        principle_type: questions[currentQuestionIndex].id,
        question_text: questions[currentQuestionIndex].question,
        user_answer: currentAnswer,
        answer_order: currentQuestionIndex + 1
      };

      const updatedAnswers = [...userAnswers, newAnswer];
      setUserAnswers(updatedAnswers);
      setCurrentAnswer('');

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // 所有问题回答完毕，保存答案并进入跨科会诊
        if (sessionId) {
          await problemDiagnosisService.saveAnswers(sessionId, updatedAnswers);
          await problemDiagnosisService.updateSession(sessionId, {
            current_step: 'crossConsult'
          });
        }
        setCurrentStep('crossConsult');
      }
    } catch (err) {
      setError('保存答案失败，请重试');
      console.error('保存答案失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!sessionId || !selectedSystem) {
      setError('会话信息不完整，无法生成报告');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const report = await problemDiagnosisService.generateReport({
        session_id: sessionId,
        problem_description: problemDescription,
        selected_system: selectedSystem.id,
        answers: userAnswers
      });

      setDiagnosisReport(report);
      setCurrentStep('report');
    } catch (err) {
      setError('生成诊断报告失败，请重试');
      console.error('生成报告失败:', err);

      // 如果 AI 生成失败，使用备用报告
      const fallbackReport: DiagnosisReport = {
        id: 'fallback',
        session_id: sessionId,
        title: `关于"${problemDescription.substring(0, 20)}..."问题的系统性诊断报告`,
        core_conclusion: `根据${selectedSystem.name}分析，该问题需要系统性解决方案。`,
        system_findings: userAnswers.map((answer, index) => ({
          system: `【${['物质性', '结构性', '关系性', '动态性', '系统性'][index]}】发现`,
          finding: answer.user_answer.substring(0, 100) + '...'
        })),
        next_steps: [
          {
            level: '第一层书架 (具体工具)',
            description: '建议采用结构化分析工具，对问题进行深入剖析。'
          },
          {
            level: '第二层书架 (领域方法论)',
            description: '建议引入系统思考方法论，重新设计解决方案。'
          },
          {
            level: '触及根本',
            description: '需要从根本层面进行系统性改进。'
          }
        ],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setDiagnosisReport(fallbackReport);
      setCurrentStep('report');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep('input');
    setProblemDescription('');
    setSelectedSystem(null);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setCurrentAnswer('');
    setDiagnosisReport(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 导航栏 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900 flex items-center">
              <Lightbulb className="w-6 h-6 text-blue-600 mr-2" />
              问题诊断系统
            </h1>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentView('main')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentView === 'main'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-1" />
                新诊断
              </button>
              <button
                onClick={() => setCurrentView('history')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentView === 'history'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <History className="w-4 h-4 inline mr-1" />
                历史记录
              </button>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {error && <ErrorMessage message={error} />}

        {/* 加载状态 */}
        {loading && <LoadingSpinner />}

        {/* 主诊断流程 */}
        {currentView === 'main' && (
          <>
        {/* 阶段零：问题输入 */}
        {currentStep === 'input' && (
          <div className="text-center">
            {/* 欢迎卡片 */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-blue-600 mb-4">💡 思考专家</h1>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                你好，我将引导你运用系统思考，从容不迫地解剖任何复杂问题。在思考"怎么办"之前，让我们先搞清楚"是什么"和"为什么"。
              </p>
              <p className="text-gray-500 mb-6">
                现在，请用一两句话，客观地描述你遇到的问题或困境。
              </p>
            </div>

            {/* 问题输入框 */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">问题描述</h2>
              </div>
              <textarea
                value={problemDescription}
                onChange={(e) => setProblemDescription(e.target.value)}
                placeholder='例如："公司新产品上市后，市场反应平平，远未达到预期。" 或 "我感觉自己的职业发展陷入了瓶颈，很迷茫。"'
                className="w-full h-32 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <p className="text-sm text-gray-500 mt-2">
                现在，请用一两句话，客观地描述你遇到的问题或困境。
              </p>
            </div>

            {/* 开始分析按钮 */}
            <button
              onClick={handleStartAnalysis}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
            >
              {loading ? '创建会话中...' : '开始分析'}
              {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
            </button>
          </div>
        )}

        {/* 阶段一：分诊挂号 */}
        {currentStep === 'triage' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">分诊挂号</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                感谢你的描述。为了找到最精准的分析切入点，我们先来"分诊挂号"。你觉得你刚才描述的问题，第一感觉更像是一个什么性质的问题？
              </p>
            </div>

            {/* 四系统选择卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {systems.map((system) => (
                <div
                  key={system.id}
                  onClick={() => handleSystemSelect(system)}
                  className={`bg-white border-2 ${system.borderColor} rounded-lg p-6 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-105`}
                >
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-lg mr-3 ${
                      system.id === 'machine' ? 'bg-blue-100 text-blue-600' :
                      system.id === 'organism' ? 'bg-green-100 text-green-600' :
                      system.id === 'social' ? 'bg-purple-100 text-purple-600' :
                      'bg-orange-100 text-orange-600'
                    }`}>
                      {system.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{system.name}</h3>
                  </div>
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {system.keywords.split('、').map((keyword, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-xs rounded-full ${
                            system.id === 'machine' ? 'bg-blue-100 text-blue-600' :
                            system.id === 'organism' ? 'bg-green-100 text-green-600' :
                            system.id === 'social' ? 'bg-purple-100 text-purple-600' :
                            'bg-orange-100 text-orange-600'
                          }`}
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{system.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 阶段二：深度剖析 */}
        {currentStep === 'analysis' && selectedSystem && (
          <div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                深度剖析 - {selectedSystem.name}
              </h1>
              <p className="text-lg text-gray-600">
                好的，我们先从【{selectedSystem.name}】这个科室入手。我将用"五大原理"这套标准的诊断协议，向你提出5个问题，请你逐一思考并回答。
              </p>
            </div>

            {/* 问题手风琴 */}
            <div className="space-y-4">
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className={`bg-white rounded-lg shadow-sm border-2 ${
                    index === currentQuestionIndex ? 'border-blue-500' : 'border-gray-200'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          index < currentQuestionIndex ? 'bg-green-600 text-white' :
                          index === currentQuestionIndex ? 'bg-blue-600 text-white' :
                          'bg-gray-200 text-gray-500'
                        }`}>
                          {index < currentQuestionIndex ? <CheckCircle className="w-4 h-4" /> : index + 1}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {question.principle}
                        </h3>
                      </div>
                      {index === currentQuestionIndex ? (
                        <ChevronUp className="w-5 h-5 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {index === currentQuestionIndex && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-gray-700 mb-4">{question.question}</p>
                        <textarea
                          value={currentAnswer}
                          onChange={(e) => setCurrentAnswer(e.target.value)}
                          placeholder={question.placeholder}
                          className="w-full h-32 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        />
                        <div className="flex justify-end mt-4">
                          <button
                            onClick={handleNextQuestion}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center"
                          >
                            下一个问题 <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </div>
                      </div>
                    )}

                    {index < currentQuestionIndex && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-gray-700 mb-2">{question.question}</p>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-600 text-sm">
                            {userAnswers.find(a => a.principle_type === question.id)?.user_answer}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 阶段三：跨科会诊 */}
        {currentStep === 'crossConsult' && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">跨科会诊</h1>
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <p className="text-gray-600 text-lg mb-6">
                基于你的回答，当前的分析已经比较完整。让我们来生成诊断报告。
              </p>
              <button
                onClick={handleGenerateReport}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-8 rounded-lg transition-colors"
              >
                {loading ? '生成报告中...' : '生成诊断报告'}
              </button>
            </div>
          </div>
        )}

        {/* 阶段四：诊断报告 */}
        {currentStep === 'report' && diagnosisReport && (
          <div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">系统性诊断报告</h1>
              <p className="text-lg text-gray-600">
                好的，经过多科室的会诊，我现在可以形成一份初步的诊断报告了。
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{diagnosisReport.title}</h2>

              {/* 核心诊断结论 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3">核心诊断结论</h3>
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                  <p className="text-gray-700">{diagnosisReport.coreConclusion}</p>
                </div>
              </div>

              {/* 分系统发现 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3">分系统发现</h3>
                <div className="space-y-3">
                  {(diagnosisReport.system_findings || []).map((finding, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="font-medium text-purple-600 mb-2">{finding.system}</h4>
                      <p className="text-gray-600 text-sm">{finding.finding}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 下一步行动建议 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3">下一步行动建议</h3>
                <div className="space-y-4">
                  {(diagnosisReport.next_steps || []).map((step, index) => (
                    <div key={index} className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                      <h4 className="font-medium text-green-600 mb-2">{step.level}</h4>
                      <p className="text-gray-600 text-sm">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-between">
                <button
                  onClick={handleRestart}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  重新分析
                </button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  保存报告
                </button>
              </div>
            </div>
          </div>
        )}
        </>
        )}

        {/* 历史记录视图 */}
        {currentView === 'history' && (
          <DiagnosisHistory
            onViewDetail={(sessionId) => {
              setSelectedSessionId(sessionId);
              setCurrentView('detail');
            }}
          />
        )}

        {/* 诊断明细视图 */}
        {currentView === 'detail' && selectedSessionId && (
          <DiagnosisDetailOptimized
            sessionId={selectedSessionId}
            onBack={() => setCurrentView('history')}
          />
        )}
      </div>
    </div>
  );
};

export default ProblemDiagnosis;
